// ─────────────────────────────────────────────────────────────
//  WaterfallChart  – Budget vs Actual vs Forecast waterfall
//  Built on Recharts ComposedChart with custom bar rendering
// ─────────────────────────────────────────────────────────────
import React from "react";
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, ResponsiveContainer, Cell, LabelList,
} from "recharts";

const POS_COLOR = "#2d8c4e";
const NEG_COLOR = "#c0392b";
const BASE_COLOR = "transparent";
const BUDGET_COLOR = "#4969ab";
const FORECAST_COLOR = "#e67e22";

// Transform raw data into waterfall segments
function buildWaterfallData(items = []) {
  let cumulative = 0;
  return items.map((item, i) => {
    const isBase  = i === 0 || i === items.length - 1;
    const value   = item.actual ?? item.value ?? 0;
    const start   = isBase ? 0 : cumulative;
    const end     = isBase ? value : cumulative + value;
    if (!isBase) cumulative += value;
    return {
      ...item,
      start,
      end,
      displayValue: value,
      isBase,
      fill: isBase ? BUDGET_COLOR : value >= 0 ? POS_COLOR : NEG_COLOR,
    };
  });
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e0e6f0",
      borderRadius: 8, padding: "10px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: "#1a2340" }}>{label}</div>
      {d?.budget !== undefined && (
        <div style={{ color: BUDGET_COLOR }}>Budget: ₹{Number(d.budget).toLocaleString("en-IN")}</div>
      )}
      {d?.actual !== undefined && (
        <div style={{ color: d.actual >= 0 ? POS_COLOR : NEG_COLOR }}>
          Actual: ₹{Number(d.actual).toLocaleString("en-IN")}
        </div>
      )}
      {d?.variance !== undefined && (
        <div style={{ color: d.variance >= 0 ? POS_COLOR : NEG_COLOR }}>
          Variance: ₹{Number(d.variance).toLocaleString("en-IN")}
          {d.variance_pct !== undefined && ` (${d.variance_pct}%)`}
        </div>
      )}
    </div>
  );
};

export default function WaterfallChart({
  data = [], title = "Budget vs Actual Waterfall",
  height = 340, showForecast = false,
}) {
  const wfData = buildWaterfallData(data);

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "18px 14px", boxShadow: "0 2px 12px rgba(73,105,171,0.08)" }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 14 }}>{title}</div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={wfData} margin={{ top: 20, right: 20, left: 10, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
          <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#666" }} angle={-35} textAnchor="end" interval={0} />
          <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} tick={{ fontSize: 11, fill: "#666" }} />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#ccc" />

          {/* invisible baseline bar */}
          <Bar dataKey="start" stackId="wf" fill="transparent" isAnimationActive={false} />

          {/* visible value bar */}
          <Bar dataKey={(d) => d.end - d.start} stackId="wf" radius={[4, 4, 0, 0]} isAnimationActive>
            {wfData.map((entry, index) => (
              <Cell key={index} fill={entry.fill} />
            ))}
            <LabelList
              content={({ x, y, width, value }) => (
                <text x={x + width / 2} y={value >= 0 ? y - 6 : y + 16}
                      fill="#555" fontSize={10} textAnchor="middle">
                  {value >= 0 ? "+" : ""}₹{Math.abs(value / 1000).toFixed(0)}K
                </text>
              )}
            />
          </Bar>

          {showForecast && (
            <Bar dataKey="forecast" fill={FORECAST_COLOR} opacity={0.6} radius={[4, 4, 0, 0]} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
